/**
 * @class
 */
import Base from '@netx/core-permissions/models/permissionResource';

import ParticipantsCollection from '@netx/core-permissions/collections/participants';

export default class SharedResource extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/**
	 * @property {object}  defaults                   - The default attribute values
	 * @property {number}  defaults.id                - The default shared resource id (attribute set id, cart id, search id etc)
	 * @property {boolean} defaults.canEdit           - The default shared resource edit permission (can change the object's criteria [assets, attrs, etc])
	 * @property {boolean} defaults.canManage         - The default shared resource manage permission (can add new participants who can r/w, and can remove participants)
	 * @property {number}  defaults.contentCount      - The default shared resource content count
	 * @property {boolean} defaults.isFavorite        - The default shared resource is favorited
	 * @property {number}  defaults.participantCount  - The default shared resource other user particpation count
	 * @property {string}  defaults.title             - The default shared resource title
	 */
	get defaults() {
		return _.extend(super.defaults, {
			// id: 0,
			canEdit: false,
			canManage: false,
			contentCount: 0,
			isFavorite: false,
			participantCount: 0,
			title: '',
		});
	}

	/**
	 * These are not client side defined, but are also not to be sent to the server when
	 * `this.self()` is called from the extending model
	 * @memberof module:share/model/sharedResource
	 * @alias clientAttrs
	 * @type {array}
	 */
	get clientAttrs() {
		return [
			'canEdit',
			'canManage',
			'contentCount',
			'isFavorite',
			'participantCount',
		];
	}

	/**
	 * @property {object} methods                              - Methods for this model
	 * @property {method} methods.read                         - Fetch shared resource
	 *
	 * @property {method} methods.sendMessageBySharedResource  - Notify user that resource has been shared with them
	 * @property {method} methods.setFavorite                  - Set/unset shared resource as favorite
	 */
	get methods() {
		return _.extend(super.methods, {
			/**
			 * @method
			 * @param {object} opts Request options
			 */
			read: function (opts) {
				return ['getSharedResource', this.resourceName, this.id];
			},

			/**
			 * Notify user that resource has been shared with them
			 * @method
			 * @param {string} message Message
			 * @param {object} userSelector User selector
			 */
			sendMessageBySharedResource: function (message, userSelector, opts) {
				return [
					'sendMessageBySharedResource',
					this.resourceName,
					this.id,
					message || '',
					userSelector,
				];
			},
			/**
			 * @method
			 * @param {boolean} favorite Favorite
			 * @param {object} opts Request options
			 */
			setFavorite: function (favorite, opts) {
				return ['setFavorite', this.resourceName, this.id, favorite || false];
			},
		});
	}

	/**
	 * @property {object}   parsers              - Parsers for this model
	 * @property {function} parsers.fetchBean    - Parse the result of full bean tied to shared resource
	 * @property {function} parsers.setFavorite  - Parse the result of set/unset favorite
	 */
	get parsers() {
		return _.extend(super.parsers, {
			/**
			 * @function
			 * @param {object} fullBean Full bean of the thing shared resource is tied to
			 * @param {object} opts Request options
			 */
			fetchBean: function (fullBean, opts) {
				this.set(fullBean, opts);
				!opts.silent && this.trigger('sync', this, fullBean, opts);
			},
			/**
			 * @function
			 * @param {boolean} resp Response (success as a boolean)
			 * @param {boolean} favorite Favorite
			 * @param {object} opts Request options
			 */
			setFavorite: function (resp, favorite, opts) {
				// If it was successful
				// Technically an unsuccessful response should be an error and so we should never hit this parser with a response of `false`
				resp && this.set('isFavorite', favorite);
			},
		});
	}

	/**
	 * @override
	 * @param {object} attrs Model attributes
	 * @param {object} opts Model options
	 */
	constructor(attrs, opts = {}) {
		super(attrs, opts);

		/**
		 * @memberof module:share/model/sharedResource
		 * @alias participants
		 * @type {module:share/collections/participants}
		 */
		let participants = opts.participants;
		if (_.isFunction(participants)) {
			participants = new participants([], {
				resourceId: this.id,
				resourceName: this.resourceName,
			});
		}
		this.participants =
			participants ||
			new ParticipantsCollection([], {
				resourceId: this.id,
				resourceName: this.resourceName,
			});
		this.addSubCollection(this.participants);

		// Hey, listen!
		this.listenTo(this, 'sync', function (model, id) {
			this.participants.resourceId = this.id;
		});
		this.listenTo(this, 'request:fetchBean', function (model, xhr, opts) {
			this.trigger('request:read', this, xhr, opts);
		});
	}

	/**
	 * @method
	 * @param {object} participant Participant object
	 * @param {object} opts Request options
	 * @return {module:permissions/model/participant} Particpant model
	 */
	addParticipant(participant, opts) {
		return this.participants.addParticipant(participant, opts);
	}

	/**
	 * ACL
	 * @method
	 * @param {string} perm Permission string ( "delete", "write/(edit)", "share", etc )
	 * @return {boolean} Allowed
	 */
	allows(perm) {
		if (perm == null) return;

		// If the data came from the server we can always read it
		if (perm === 'read') return true;

		switch (perm) {
			// Can delete saved cart
			case 'delete':
				return this.canManage();
			// Can download
			// DAM-6555.  (Allow access to download; the download process will reveal what can/can't be downloaded.)
			case 'download':
				return true;
			// Can manage user access to shared resource
			case 'manage':
			case 'share':
				return this.canManage();
			// Can add/remove items from shared resource
			case 'edit':
			case 'write':
				return this.canManage() || this.canEdit();
		}
	}

	/**
	 * @method
	 * @return {boolean}
	 */
	canEdit() {
		var perm = this.get('permission');

		return (
			this.get('canEdit') === true || perm === 'delete' || perm === 'write'
		);
	}

	/**
	 * @method
	 * @return {boolean}
	 */
	canManage() {
		var perm = this.get('permission');

		return this.get('canManage') === true || perm === 'delete';
	}

	/**
	 * Shared resources use participants
	 * We should try and unify the two and have particpants be how all
	 * permissions are handled
	 * @override
	 * @param {object} opts Request options
	 */
	getPermissions(opts) {
		return this.getParticipants.apply(this, arguments);
	}

	/**
	 * @method
	 * @param {object} opts Request options
	 */
	getParticipants(opts) {
		if (this.isNew()) return new $.Deferred().resolveWith(this.participants);

		return this.participants.fetch(opts);
	}

	/**
	 * @method
	 * @return {boolean}
	 */
	isFavorite() {
		return this.get('isFavorite') === true;
	}

	/**
	 * @method
	 * @param {number} id Participant id
	 * @param {object} opts Request options
	 * @return {module:permissions/model/participant} Particpant model
	 */
	removeParticipant(id, opts) {
		return this.participants.removeParticipant(id, opts);
	}

	/**
	 * @method
	 * @param {string} message Message
	 * @param {object} userSelector User selector
	 * @param {object} opts Options
	 */
	sendMessage(message, userSelector, opts) {
		return this.sendMessageBySharedResource(message, userSelector, opts);
	}

	/**
	 * @method
	 * @param {object} opts Request options
	 */
	toggleFavorite(opts) {
		var favorite = !this.isFavorite();
		return this.setFavorite(favorite, opts).then(function () {
			return this.isFavorite();
		});
	}

	/** @type {string} */
	static get __name__() {
		return 'SharedResource';
	}
}
